import {Helmet} from '@dr.pogodin/react-helmet';
import {Action, Card, DataStatus, Form, FormField, openDeleteDialog} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {isNil, reject} from 'ramda';
import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {TaxDocumentCalculationResponseBodyRead} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {
  useCancelCashRegisterDocumentMutation,
  useGetCashReceiptV4Query,
  useGetCashRegisterQuery,
  useRenderExpenseCashReceiptDocumentMutation,
  useRenderIncomeCashReceiptDocumentMutation,
} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {downloadFile, handleApiError, useGetAccountingDocuments, usePermissions} from '@dms/shared';

import {buildArray, useRequiredParams} from 'shared/web';

import {BasicInformationDetail} from '../../components/BasicInformationDetail';
import {CustomerAndSupplierDetail} from '../../components/CustomerAndSupplierDetail';
import {InvoiceDetailHeader} from '../../components/InvoiceDetailHeader';
import {InvoiceDetailSubHeader} from '../../components/InvoiceDetailSubHeader';
import {InvoicePage} from '../../components/InvoicePage';
import {ListOfItemsDetail} from '../../components/ListOfItemsDetail';
import {PairedInvoice} from '../../components/PairedInvoice/PairedInvoice';
import {AccountingCalculationCacheProvider} from '../../context/AccountingCalculationCacheProvider';
import {InvoiceVatRateProvider} from '../../context/InvoiceVatRateProvider';
import {AccountingDocumentFormValues} from '../../types';
import {getDocumentSourceFlags} from '../../utils/getDocumentSourceFlags';
import {
  convertCashRegisterItemToInvoiceItem,
  covertIssuerToSupplier,
  getCashReceiptStateFlagProps,
  useGetDefaultValuesFromCashReceipt,
} from './utils/utils';

export function CashReceiptDetail() {
  return (
    <AccountingCalculationCacheProvider>
      <CashReceiptDetailContent />
    </AccountingCalculationCacheProvider>
  );
}

function CashReceiptDetailContent() {
  const {id: cashReceiptId} = useRequiredParams();

  const [renderIncomeCashReceipt, {isLoading: isLoadingIncomeRender}] =
    useRenderIncomeCashReceiptDocumentMutation();
  const [renderExpenseCashReceipt, {isLoading: isLoadingExpenseRender}] =
    useRenderExpenseCashReceiptDocumentMutation();
  const [cancelCashReceiptDocument] = useCancelCashRegisterDocumentMutation();
  const [canReadInvoices, canDownloadDocuments, canCancelCashRegisterDocument] = usePermissions({
    permissionKeys: ['readInvoice', 'downloadDocuments', 'cancelCashRegisterDocument'],
  });

  const {
    data: cashReceipt,
    isLoading: isLoadingCashReceipt,
    isError: isCashReceiptError,
  } = useGetCashReceiptV4Query({cashRegisterDocumentId: cashReceiptId});

  const {data: invoiceDocument} = useGetAccountingDocuments(
    cashReceipt?.relatedInvoicingDocumentId,
    cashReceipt?.relatedInvoicingDocumentType
  );

  const downloadCashReceipt = async () => {
    const renderAction =
      cashReceipt?.type === 'expense'
        ? renderExpenseCashReceipt({
            renderDocumentRequestBody: {cashRegisterDocumentId: cashReceiptId},
          })
        : renderIncomeCashReceipt({
            renderDocumentRequestBody: {cashRegisterDocumentId: cashReceiptId},
          });

    await renderAction
      .unwrap()
      .then((data) => data.pdfUrl)
      .then(downloadFile)
      .catch(handleApiError);
  };

  const handleCancelDocument = () => {
    const issuedAt = new Date().toISOString();
    openDeleteDialog({
      'data-testid': testIds.accounting.cashReceiptDetail('delete-dialog'),
      text:
        cashReceipt?.type === 'income'
          ? i18n.t('entity.cashRegister.notifications.cancelIncomeCashRegisterDocument', {
              documentNumber: cashReceipt?.number,
            })
          : i18n.t('entity.cashRegister.notifications.cancelExpenseCashRegisterDocument', {
              documentNumber: cashReceipt?.number,
            }),
      onConfirm: () =>
        cancelCashReceiptDocument({
          cashRegisterDocumentId: cashReceiptId,
          cancelCashRegisterDocumentRequestBody: {issuedAt},
        })
          .unwrap()
          .catch(handleApiError),
    });
  };

  const {data: userData} = useGetUserQuery(
    {id: cashReceipt?.createdById ?? ''},
    {skip: isNil(cashReceipt?.createdById)}
  );

  const {isLoading: isLoadingCashRegister} = useGetCashRegisterQuery(
    {cashRegisterId: cashReceipt?.cashRegisterId ?? ''},
    {skip: isNil(cashReceipt)}
  );

  const authorName = reject(isNilOrEmpty, [userData?.firstName, userData?.lastName]).join(' ');

  const pageTitle = i18n.t(
    cashReceipt?.type === 'income'
      ? 'page.accounting.cashRegister.detailIncomeNumber'
      : 'page.accounting.cashRegister.detailExpenseNumber',
    {
      documentNumber: cashReceipt?.number,
    }
  );

  const buttons = buildArray<Action>()
    .when(canDownloadDocuments, {
      type: 'button',
      title: i18n.t('general.actions.generatePDF'),
      onClick: downloadCashReceipt,
      isLoading: isLoadingExpenseRender || isLoadingIncomeRender,
      leftIcon: 'editor/insert_drive_file',
      variant: 'link',
      size: 'small',
    })
    .when(cashReceipt?.state === 'taken' && canCancelCashRegisterDocument, {
      type: 'button',
      title: i18n.t('entity.checkout.actions.createCorrectiveTaxDocument'),
      onClick: handleCancelDocument,
      leftIcon: 'action/delete',
      variant: 'dangerLink',
      size: 'small',
    });

  const stateFlags = cashReceipt ? [getCashReceiptStateFlagProps(cashReceipt.state)] : undefined;

  const defaultValues = useGetDefaultValuesFromCashReceipt(cashReceipt);

  const isLoading = isLoadingCashReceipt || isLoadingCashRegister;
  const isError = isCashReceiptError;

  const summary: TaxDocumentCalculationResponseBodyRead | null = cashReceipt
    ? {
        ...cashReceipt.cashReceiptSummary,
        vatTypesSummary:
          cashReceipt.exchangeRateRatioCashReceiptSummary?.vatTypesSummary ??
          cashReceipt.cashReceiptSummary?.vatTypesSummary,
      }
    : null;

  return (
    <>
      <Helmet title={pageTitle} />
      <DataStatus minHeight="80vh" isLoading={isLoading} isError={isError}>
        <Form<AccountingDocumentFormValues> defaultValues={defaultValues}>
          {(control, formApi) => (
            <InvoiceVatRateProvider control={control} listItemType="cash-receipts">
              <VStack>
                <InvoiceDetailHeader
                  title={pageTitle}
                  data-testid={testIds.accounting.cashReceiptDetail('title')}
                />
                <InvoiceDetailSubHeader
                  author={authorName}
                  flags={stateFlags}
                  subFlags={getDocumentSourceFlags(cashReceipt?.documentSource)}
                  actions={buttons}
                  data-testid={testIds.accounting.cashReceiptDetail('invoice-detail-subHeader')}
                  createdAt={cashReceipt?.createdAt}
                />

                <InvoicePage>
                  <Show when={cashReceipt}>
                    {(cashReceipt) => (
                      <Card>
                        <CustomerAndSupplierDetail
                          supplier={covertIssuerToSupplier(cashReceipt.issuer)}
                          // The backend stores an empty customer on receipts without one, so customerId decides whether to show it.
                          customer={isNotNil(cashReceipt.customerId) ? cashReceipt.customer : null}
                          data-testid={testIds.accounting.cashReceiptDetail('customerAndSupplier')}
                        />
                      </Card>
                    )}
                  </Show>

                  <Show when={canReadInvoices}>
                    <PairedInvoice
                      control={control}
                      formApi={formApi}
                      invoiceDocument={invoiceDocument}
                      invoiceType={cashReceipt?.relatedInvoicingDocumentType}
                      isDetail
                      data-testid={testIds.accounting.cashReceiptDetail('paired-invoice')}
                    />
                  </Show>

                  <BasicInformationDetail
                    control={control}
                    cashReceipt={cashReceipt}
                    data-testid={testIds.accounting.cashReceiptDetail('basic-information')}
                  />

                  <Show
                    when={
                      isNotNil(cashReceipt) && isNotNil(summary) ? {cashReceipt, summary} : null
                    }
                  >
                    {(receipt) => (
                      <ListOfItemsDetail
                        items={receipt.cashReceipt.items.map(convertCashRegisterItemToInvoiceItem)}
                        summary={receipt.summary}
                        exchangeRateRatioCalculation={
                          receipt.cashReceipt.exchangeRateRatioCashReceiptSummary
                        }
                        cashRegisterId={receipt.cashReceipt.cashRegisterId}
                        control={control}
                        isCashReceipt
                        data-testid={testIds.accounting.cashReceiptDetail('list-of-items-detail')}
                      />
                    )}
                  </Show>

                  <Card>
                    <FormField
                      control={control}
                      name="footerNote"
                      type="textarea"
                      isDisabled
                      label={i18n.t('general.labels.note')}
                      data-testid={testIds.accounting.cashReceiptDetail('footerNote')}
                    />
                  </Card>
                </InvoicePage>
              </VStack>
            </InvoiceVatRateProvider>
          )}
        </Form>
      </DataStatus>
    </>
  );
}
